/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { entitySwaggerToEntityStore } from 'src/luis/core/services/entities/mappers';
import { HierarchicalEntitySwagger } from 'src/luis/projects/entities/types/swagger/HierarchicalEntitySwagger';
import { LuisId } from 'src/luis/types/Resource';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';

/**
 * Represents a service to perform CRUD operations for hierarchical entity.
 */
export const getHierarchicalEntityService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url builder for this service.
     *
     * @param entityId An optional entity id to add.
     * @returns A url string to call.
     */
    const getUrlBuilder = (entityId: LuisId = '') =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('hierarchicalEntities', entityId);

    /**
     * Gets the hierarchical entity with provided id.
     */
    const getEntity = async ({ id }: Pick<HierarchicalEntityStore, 'id'>): Promise<HierarchicalEntityStore> => {
        const requestUrl = getUrlBuilder(id).build();
        const { data: entity } = await httpService.get<HierarchicalEntitySwagger>(requestUrl, apiOptions);

        return <HierarchicalEntityStore>entitySwaggerToEntityStore(entity);
    };

    /**
     * Deletes the given hierarchical entity.
     *
     * @param id The id of the hierarchical entity to delete.
     */
    const deleteEntity = async ({ id }: Pick<HierarchicalEntityStore, 'id'>): Promise<void> => {
        const requestUrl = getUrlBuilder(id).build();
        await httpService.delete(requestUrl, apiOptions);
    };

    /**
     * Renames the given hierarchical entity.
     *
     * @param id The id of the entity to update.
     * @param name The hierarchical entity new name.
     * @param children The hierarchical entity children.
     */
    const renameEntity = async ({
        id,
        name,
        children
    }: Pick<HierarchicalEntityStore, 'name' | 'id'> & { children: string[] }): Promise<void> => {
        const requestUrl = getUrlBuilder(id).build();
        await httpService.put(requestUrl, { name, children }, apiOptions);
    };

    /**
     * Renames a new child for given hierarchical entity.
     *
     * @param id The hierarchical entity id.
     * @param childId The hierarchical entity child id.
     * @param name  The name of the new hierarchical child model.
     *
     * @returns The id of newly added child entity.
     */
    const renameChildEntity = async ({
        id,
        name,
        childId
    }: Pick<HierarchicalEntityStore, 'id'> & { name: string; childId: LuisId }): Promise<void> => {
        const requestUrl = getUrlBuilder(id)
            .resource('children', `${childId}`)
            .build();
        await httpService.put(requestUrl, { name }, apiOptions);
    };

    /**
     * Deletes the given child from hierarchical entity.
     *
     * @param id The id of the hierarchical entity to delete child from.
     * @param id The id of the child entity to delete.
     */
    const deleteChildEntity = async ({ id, childId }: Pick<HierarchicalEntityStore, 'id'> & { childId: LuisId }): Promise<void> => {
        const requestUrl = getUrlBuilder(id)
            .resource('children', `${childId}`)
            .build();
        await httpService.delete(requestUrl, apiOptions);
    };

    return { getEntity, renameEntity, deleteEntity, renameChildEntity, deleteChildEntity };
});
